import {useParams} from "react-router-dom";
import QueryErrorAlert from "../../../components/QueryErrorAlert";
import React, {useEffect, useState} from "react";
import Page, {
    Content,
    ContentTitle,
    LabeledSection,
    LabeledValue,
    TitleBarButton,
} from "../../../components/Layout/Page";
import {formatTimestamp} from "../../../utils/datetime";
import {useOrderCancelMutation, useOrderGetQuery, useOrderShipmentCreateMutation} from "../../../app/api/api";
import {useTranslation} from "react-i18next";
import {Order, OrderField, OrderItem, OrderItemField, OrderStatus} from "../../../app/api/model/order";
import {Grid, Typography} from "@mui/material";
import {ColumnDef} from "../../../components/ExTable";
import SimpleTable from "../../../components/ExTable/SimpleTable";
import CreateIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import {useSnackbar} from "notistack";
import {DeleteForever, InfoOutlined} from "@mui/icons-material";
import OrderCustomerNameDialog from "../components/OrderCustomerNameDialog";
import {useModal} from "../../../hooks/useModal";
import {Organization} from "../../../app/api/model/organization";
import {Colors} from "../../../components/Layout/Colors";
import OrderDetailsShippingRecipientForm from "./Form/OrderDetailsShippingtRecipientForm";
import OrderDetailsAdminUserForm from "./Form/OrderDetailsAdminUserForm";
import {EMPTY_UI} from "../../../variables";
import OrderDetailsCommentForm from "./Form/OrderDetailsCommentForm";
import {getTranslation, TranslationContext} from "../../../utils/getTranslationKey";
import {getStatusLabel} from "../helpers";
import CancelDialog from "../../../components/Dialog/CancelDialog";
import {StageIdentifier} from "../../../app/api/model/stage";
import OrganizationCreateFromOrder from "./Form/OrganizationCreateFromOrder";
import {AddressField} from "../../../app/api/model/address";
import {CustomerField} from "../../../app/api/model/customer";
import {ShippingRecipientField} from "../../../app/api/model/shipment";
import {AdminUserField} from "../../../app/api/model/adminUser/adminUser";
import {usePermission} from "../../../hooks/usePermission";
import {PermissionLevel} from "../../../app/api/model/user";

const getOrganizationDataFromOrder = (order?: Order) =>
    order
        ? ({
              id: 0, // defines that a new organization will be created instead of updating an existing one
              name: order?.[OrderField.CUSTOMER]?.[CustomerField.NAME],
              country: order?.[OrderField.SHIPPING_RECIPIENT]?.[ShippingRecipientField.ADDRESS]?.[AddressField.COUNTRY],
              locked: false,
              lockedReason: "",
              productiveStage: StageIdentifier.LEGACY,
              adminLanguage: `${order?.[OrderField.ADMIN_USER]?.[AdminUserField.LANGUAGE]}`.toUpperCase(),
              adminEmail: order?.[OrderField.ADMIN_USER]?.[AdminUserField.EMAIL],
              mainDKVCustomerNumber: `${order?.[OrderField.CUSTOMER]?.[CustomerField.CUSTOMER_NUMBER]}`,
              additionalDKVCustomerNumbers: "",
          } as Organization)
        : null;

export default function OrderDetails() {
    const params = useParams();
    const orderId = parseInt(params.orderId || "");
    const {data: order, error} = useOrderGetQuery(orderId);
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [isCustomerNameDialogOpen, setIsCustomerNameDialogOpen] = useState(false);
    const {hasPermission} = usePermission();

    const itemsColumnsDef: ColumnDef<OrderItem>[] = [
        {
            fieldName: OrderItemField.ALREADY_HANDED_OUT,
            label: t("order_itemAlreadyHandedOut"),
            sortable: false,
            align: "left",
            formatCellText: ({cellValue}) => {
                return cellValue ? t("generic_yes") : t("generic_no");
            },
        },
        {
            fieldName: OrderItemField.BRANDING,
            label: t("order_itemBranding"),
            sortable: false,
            align: "left",
        },
        {
            fieldName: OrderItemField.CONNECTION_TYPE,
            label: t("order_itemConnectionType"),
            sortable: false,
            align: "left",
        },
        {
            fieldName: OrderItemField.LEO_ID,
            label: t("generic_leoId"),
            sortable: false,
            align: "left",
        },
        {
            fieldName: OrderItemField.SERIAL_NUMBER,
            label: t("order_itemSerialNumber"),
            sortable: false,
            align: "left",
            formatCellText: ({cellValue}) => cellValue ?? "-",
        },
        {
            fieldName: OrderItemField.TYPE,
            label: t("order_itemType"),
            sortable: false,
            align: "left",
        },
    ];
    const createOrganizationModal = useModal(false);
    const updateShippingRecipientModal = useModal(false);
    const cancelOrderModal = useModal(false);
    const [orderCancel] = useOrderCancelMutation();

    const updateAdminUserModal = useModal(false);
    const updateCommentModal = useModal(false);
    const [shipmentCreate, {isLoading: isCreatingShipment, data: shipmentData}] = useOrderShipmentCreateMutation();

    const notCanceled = order?.status !== OrderStatus.CANCELED;

    useEffect(() => {
        if (shipmentData) {
            enqueueSnackbar(t("shipment_createdSuccessfully"), {variant: "success"});
        }
    }, [JSON.stringify(shipmentData)]);

    if (error) {
        return <QueryErrorAlert error={error} />;
    }

    const buttons: TitleBarButton[] = [];
    if (notCanceled && order?.shipmentCreatedAt === null && order.customer.organizationId) {
        buttons.push({
            key: "createShipment",
            tooltip: "action_createShipmentFromOrder",
            disabled: !!shipmentData || isCreatingShipment || order.shipmentCreatedAt,
            minRequiredPermissionToSee: PermissionLevel.MANAGER,
            callback: () => shipmentCreate(order.id),
            icon: <CreateIcon />,
        });
    }

    if (notCanceled && order?.status === OrderStatus.NEW) {
        buttons.push({
            key: "cancelOrder",
            tooltip: "action_cancelOrder",
            disabled: false,
            minRequiredPermissionToSee: PermissionLevel.MANAGER,
            callback: () => cancelOrderModal.setIsOpen(true),
            icon: <DeleteForever />,
        });
    }

    if (notCanceled && !order?.customer?.organizationId) {
        buttons.push({
            key: "createOrganization",
            tooltip: "action_createOrganization",
            disabled: false,
            minRequiredPermissionToSee: PermissionLevel.MANAGER,
            callback: () => createOrganizationModal.setIsOpen(true),
            icon: <CreateIcon />,
        });
    }

    const organization = getOrganizationDataFromOrder(order);

    return (
        <Page>
            <ContentTitle
                titleKey="generic_order"
                subtitle={order?.orderNumber}
                buttons={buttons}
                entry={order}
                toolEntries={[
                    {
                        translationKey: "generic_shipmentCreatedAt",
                        fieldName: OrderField.SHIPMENT_CREATED_AT,
                        render: (shipmentCreatedAt) => formatTimestamp(shipmentCreatedAt),
                    },
                ]}
            />
            {order && (
                <Content columns={2}>
                    <LabeledSection
                        title={t("order_generalInformation")}
                        {...(notCanceled &&
                            order?.customer.nameMismatch && {
                                buttons: [
                                    {
                                        key: "updateCustomerName",
                                        tooltip: "action_updateCustomerName",
                                        disabled: isCustomerNameDialogOpen || !hasPermission(PermissionLevel.MANAGER),
                                        minRequiredPermissionToSee: PermissionLevel.MANAGER,
                                        callback: () => setIsCustomerNameDialogOpen(true),
                                        icon: <InfoOutlined />,
                                    },
                                ],
                            })}
                    >
                        <LabeledValue label={t("order_orderNumber")} content={order.orderNumber} />
                        <LabeledValue
                            label={t("generic_customerNumber")}
                            content={order?.customer?.customerNumber}
                            color={!order?.customer?.organizationId ? Colors.RED : Colors.BLACK}
                        />
                        <LabeledValue
                            label={t("generic_status")}
                            content={getStatusLabel(order.status, t, TranslationContext.ORDER_STATUS)?.node}
                        />
                        <LabeledValue
                            label={t("generic_customerName")}
                            content={order?.customer?.name}
                            color={
                                !order?.customer?.organizationId || order?.customer.nameMismatch
                                    ? Colors.RED
                                    : Colors.BLACK
                            }
                        />
                        <LabeledValue
                            label={t("generic_legacy")}
                            content={order?.[OrderField.LEGACY] ? t("generic_yes") : t("generic_no")}
                        />
                        <LabeledValue label={t("generic_organizationId")} content={order?.customer?.organizationId} />
                    </LabeledSection>
                    <LabeledSection
                        title={t("order_shippingRecipient")}
                        {...(notCanceled &&
                            !order?.shipmentCreatedAt &&
                            hasPermission(PermissionLevel.MANAGER) && {
                                buttons: [
                                    {
                                        key: "updateShippingRecipient",
                                        tooltip: "action_updateShippingRecipient",
                                        disabled: updateShippingRecipientModal.isOpen,
                                        minRequiredPermissionToSee: PermissionLevel.MANAGER,
                                        callback: () => updateShippingRecipientModal.setIsOpen(true),
                                        icon: <EditIcon />,
                                    },
                                ],
                            })}
                    >
                        <LabeledValue
                            label={t("order_recipient")}
                            content={`${order?.shippingRecipient.contact.firstName || EMPTY_UI} ${
                                order?.shippingRecipient.contact.lastName || EMPTY_UI
                            }`}
                        />
                        <LabeledValue label={t("generic_email")} content={order.shippingRecipient.contact.email} />
                        <LabeledValue label={t("generic_phone")} content={order.shippingRecipient.contact.phone} />
                        <LabeledValue label={t("generic_company")} content={order?.shippingRecipient.companyName} />
                        <LabeledValue
                            label={t("generic_street")}
                            content={`${order?.shippingRecipient.address.street} ${order?.shippingRecipient.address.houseNumber}`}
                        />
                        <LabeledValue
                            label={t("generic_addressPostalCode")}
                            content={`${order?.shippingRecipient.address.postalCode}`}
                        />
                        <LabeledValue
                            label={t("generic_addressAddition")}
                            content={`${order?.shippingRecipient.address.addressAddition}`}
                        />
                        <LabeledValue label={t("generic_city")} content={`${order?.shippingRecipient.address.city}`} />
                        <LabeledValue
                            label={t("generic_country")}
                            content={`${order?.shippingRecipient.address.country}`}
                        />
                    </LabeledSection>
                    <LabeledSection
                        title={t("order_adminUser")}
                        {...(notCanceled &&
                            !order?.customer?.organizationId &&
                            !order?.shipmentCreatedAt &&
                            hasPermission(PermissionLevel.MANAGER) && {
                                buttons: [
                                    {
                                        key: "updateAdminUser",
                                        tooltip: "action_updateAdminUser",
                                        disabled: updateAdminUserModal.isOpen,
                                        minRequiredPermissionToSee: PermissionLevel.MANAGER,
                                        callback: () => updateAdminUserModal.setIsOpen(true),
                                        icon: <EditIcon />,
                                    },
                                ],
                            })}
                    >
                        <LabeledValue
                            label={t("order_adminName")}
                            content={`${order.adminUser.firstName} ${order.adminUser.lastName}`}
                        />
                        <LabeledValue label={t("generic_email")} content={order.adminUser.email} />
                        <LabeledValue label={t("generic_phone")} content={order.adminUser.phone} />
                        <LabeledValue
                            label={t("generic_language")}
                            content={getTranslation({
                                context: TranslationContext.LANGUAGE,
                                value: order.adminUser.language,
                                t,
                            })}
                        />
                        <LabeledValue label={t("generic_timeZone")} content={order.adminUser.timeZone} />
                    </LabeledSection>
                    {!notCanceled && (
                        <LabeledSection title={t("generic_cancelReason")} xs={12}>
                            <Typography variant={"body1"}>{order.cancelReason || EMPTY_UI}</Typography>
                        </LabeledSection>
                    )}
                    <LabeledSection
                        title={t("generic_comment")}
                        xs={12}
                        sx={{overflowWrap: "anywhere", maxWidth: "100%"}}
                        buttons={
                            notCanceled
                                ? [
                                      {
                                          key: "updateComment",
                                          tooltip: "action_updateComment",
                                          disabled: updateCommentModal.isOpen,
                                          minRequiredPermissionToSee: PermissionLevel.MANAGER,
                                          callback: () => updateCommentModal.setIsOpen(true),
                                          icon: <EditIcon />,
                                      },
                                  ]
                                : undefined
                        }
                    >
                        <Typography sx={{paddingTop: "12px"}}>{order.comment ?? EMPTY_UI}</Typography>
                    </LabeledSection>
                    <LabeledSection title={t("order_items")} xs={12}>
                        {order?.items.length ? (
                            <SimpleTable columnDefs={itemsColumnsDef} entries={order.items} />
                        ) : (
                            <Grid item xs={12} key={"order_items"}>
                                <Typography>{t("order_noItems")}</Typography>
                            </Grid>
                        )}
                    </LabeledSection>
                    {notCanceled && (
                        <>
                            <OrderDetailsShippingRecipientForm modal={updateShippingRecipientModal} order={order} />
                            <OrderDetailsAdminUserForm modal={updateAdminUserModal} order={order} />
                            <OrderDetailsCommentForm
                                modal={updateCommentModal}
                                order={order}
                                onSuccess={updateCommentModal.onClose}
                            />

                            <CancelDialog
                                entity={order}
                                isOpen={cancelOrderModal.isOpen}
                                onSuccess={cancelOrderModal.onClose}
                                onClose={cancelOrderModal.onClose}
                                title={t("order_cancelOrder")}
                                bodyText={t("order_cancelInformation")}
                                titleCancel={t("action_cancelOrder")}
                                onSubmit={(requestData) => orderCancel(requestData).unwrap()}
                            />
                        </>
                    )}
                </Content>
            )}
            {order?.id && notCanceled && organization && (
                <OrganizationCreateFromOrder
                    orderId={order.id}
                    organization={organization}
                    modal={createOrganizationModal}
                />
            )}
            {notCanceled && (
                <OrderCustomerNameDialog
                    open={isCustomerNameDialogOpen}
                    onClose={() => setIsCustomerNameDialogOpen(false)}
                    customerName={order?.customer?.name}
                    dkvCustomerNumber={order?.customer.customerNumber}
                    dkvCustomerName={order?.customer?.nameAssociatedWithNumber ?? undefined}
                    orderId={order?.id}
                />
            )}
        </Page>
    );
}
